import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Droplets, Plus, Trash2, Settings, Target } from 'lucide-react';
import api from '@/lib/api';
import { formatTime } from '@/lib/datetime';

const presets = [250, 500, 750, 1000];

export default function CustomerHydration() {
    const [goal, setGoal] = useState(2000);
    const [isDefaultGoal, setIsDefaultGoal] = useState(true);
    const [logs, setLogs] = useState([]);
    const [consumed, setConsumed] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [goalForm, setGoalForm] = useState(2000);
    const [customAmount, setCustomAmount] = useState('');
    const [savingGoal, setSavingGoal] = useState(false);
    const [adding, setAdding] = useState(false);

    const fetchData = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setInitialLoading(true);
        setError('');
        try {
            const [goalRes, logsRes] = await Promise.all([
                api.get('/customer/water/goal'),
                api.get('/customer/water/logs', { params: { date: selectedDate } }),
            ]);
            setGoal(goalRes.data.data.goal_ml);
            setIsDefaultGoal(goalRes.data.data.is_default);
            setGoalForm(goalRes.data.data.goal_ml);

            const d = logsRes.data.data;
            setConsumed(d.consumed_ml);
            setPercentage(d.percentage);
            setLogs(d.logs);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar datos.');
        } finally {
            if (!silent) setInitialLoading(false);
        }
    }, [selectedDate]);

    useEffect(() => { fetchData(); }, []);
    useEffect(() => { fetchData({ silent: true }); }, [selectedDate]);

    const recalc = (newLogs, newGoal) => {
        const total = newLogs.reduce((sum, l) => sum + l.consumed_ml, 0);
        const pct = newGoal > 0 ? Math.round(Math.min(100, (total / newGoal) * 100)) : 0;
        setConsumed(total);
        setPercentage(pct);
    };

    const handleAdd = async (amount) => {
        if (!amount || amount <= 0) return;
        setAdding(true);
        try {
            const res = await api.post('/customer/water/logs', { consumed_ml: amount, logged_at: selectedDate });
            const newLog = res.data.data;
            const updated = [newLog, ...logs];
            setLogs(updated);
            recalc(updated, goal);
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar.');
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Eliminar este registro?')) return;
        try {
            await api.delete(`/customer/water/logs/${id}`);
            const updated = logs.filter(l => l.id !== id);
            setLogs(updated);
            recalc(updated, goal);
        } catch (err) {
            alert(err.response?.data?.message || 'Error al eliminar.');
        }
    };

    const handleSaveGoal = async (e) => {
        e.preventDefault();
        setSavingGoal(true);
        try {
            await api.put('/customer/water/goal', { goal_ml: Number(goalForm) });
            const newGoal = Number(goalForm);
            setGoal(newGoal);
            setIsDefaultGoal(false);
            recalc(logs, newGoal);
            setDialogOpen(false);
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar la meta.');
        } finally {
            setSavingGoal(false);
        }
    };

    if (initialLoading) return <p className="text-white/60 text-center py-8">Cargando...</p>;

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Hidratación</h1>
                <div className="flex items-center gap-2">
                    <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-auto bg-[#0a0a0a] border-[#3a3a3f] text-white"
                    />
                    <Button variant="outline" size="icon" onClick={() => setDialogOpen(true)} className="border-[#3a3a3f] text-white hover:bg-white/5">
                        <Settings className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {error && (
                <div className="bg-white/5 text-white text-sm p-3 rounded-md mb-4">{error}</div>
            )}

            {/* Progress card */}
            <Card className="bg-[#0a0a0a] border-hairline-dark mb-6">
                <CardContent className="py-8">
                    <div className="flex flex-col items-center text-center">
                        <div className="relative h-40 w-40 mb-4">
                            <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36">
                                <path
                                    className="text-[#3a3a3f]"
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                />
                                <path
                                    className="text-white transition-all duration-500 ease-out"
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeDasharray={`${percentage}, 100`}
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <Droplets className="h-6 w-6 text-white mb-1" />
                                <span className="text-2xl font-bold text-white">{percentage}%</span>
                            </div>
                        </div>
                        <p className="text-white/60 text-sm mb-1">
                            <span className="font-semibold text-white">{consumed} ml</span> de <span className="font-semibold text-white">{goal} ml</span>
                        </p>
                        {isDefaultGoal && (
                            <p className="text-white/40 text-xs">Meta por defecto (2000 ml). Podés personalizarla.</p>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Quick add */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {presets.map(p => (
                    <Button key={p} variant="outline" disabled={adding} onClick={() => handleAdd(p)} className="border-[#3a3a3f] text-white hover:bg-white/5 justify-center">
                        <Plus className="h-4 w-4 mr-1" />{p} ml
                    </Button>
                ))}
            </div>

            {/* Custom add */}
            <div className="flex items-center gap-2 mb-6">
                <Input
                    type="number"
                    placeholder="Cantidad personalizada (ml)"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/30"
                />
                <Button onClick={() => { handleAdd(Number(customAmount)); setCustomAmount(''); }} disabled={adding || !customAmount || Number(customAmount) <= 0}>
                    <Plus className="h-4 w-4 mr-1" />Agregar
                </Button>
            </div>

            {/* Logs list */}
            <h2 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-3">Registros del día</h2>
            {logs.length === 0 ? (
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="py-8 text-center">
                        <Droplets className="h-10 w-10 text-white/60 mx-auto mb-3" />
                        <p className="text-white/60 text-sm">No hay registros de agua para este día.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2">
                    {logs.map(log => (
                        <div key={log.id} className="flex items-center justify-between p-3 rounded-lg border border-[#3a3a3f] bg-[#0a0a0a]">
                            <div className="flex items-center gap-3">
                                <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                    <Droplets className="h-4 w-4" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-white">{log.consumed_ml} ml</p>
                                    <p className="text-xs text-white/60">{formatTime(log.logged_at, { hour: '2-digit', minute: '2-digit' })}</p>
                                </div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(log.id)} className="text-white hover:bg-white/5">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {/* Goal dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
                            <Target className="h-4 w-4" />Meta diaria de agua
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveGoal} className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Meta (ml)</Label>
                            <Input
                                type="number"
                                value={goalForm}
                                onChange={(e) => setGoalForm(e.target.value)}
                                min={500}
                                max={10000}
                                step={100}
                                required
                                className="bg-[#0a0a0a] border-[#3a3a3f] text-white"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cancelar</Button>
                            <Button type="submit" disabled={savingGoal}>{savingGoal ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
